import apiClient, { handleUnauthorizedResponse } from "./apiClient";
import i18n from "../assets/translations/i18n";

/**
 * Admin Service
 * Handles all admin-related API calls for items, customers, users, payment methods, and pricing
 */

// Items

export async function getItems(
  companyId: string,
  options: {
    search?: string;
    includeArchived?: boolean;
    page?: number;
    pageSize?: number;
    outletId?: string;
    categoryId?: string;
  } = {},
) {
  const params = new URLSearchParams();
  if (options.search) params.append("q", options.search);
  if (options.includeArchived) params.append("includeArchived", "true");
  params.append("page", String(options.page || 1));
  params.append("pageSize", String(options.pageSize || 20));
  if (options.outletId) params.append("outletId", options.outletId);
  if (options.categoryId) params.append("categoryId", options.categoryId);

  const response = await apiClient.get(
    `/api/companies/${companyId}/items?${params.toString()}`,
  );
  return response as {
    items: any[];
    total: number;
    page: number;
    pageSize: number;
  };
}

export async function getItem(companyId: string, itemId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/items/${itemId}`,
  );
  return response.item;
}

export async function createItem(companyId: string, itemData: any) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/items`,
    itemData,
  );
  return response.item;
}

export async function updateItem(
  companyId: string,
  itemId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/items/${itemId}`,
    updates,
  );
  return response.item;
}

export async function archiveItem(companyId: string, itemId: string) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/items/${itemId}/archive`,
  );
  return response.item;
}

export async function unarchiveItem(companyId: string, itemId: string) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/items/${itemId}/unarchive`,
  );
  return response.item;
}

function buildExportParams(options: {
  search?: string;
  includeArchived?: boolean;
  outletId?: string;
  categoryId?: string;
}) {
  const params = new URLSearchParams();
  if (options.search) params.append("q", options.search);
  if (options.includeArchived) params.append("includeArchived", "true");
  if (options.outletId) params.append("outletId", options.outletId);
  if (options.categoryId) params.append("categoryId", options.categoryId);
  return params;
}

function extractFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match ? match[1] : fallback;
}

export async function exportItemsCsv(
  companyId: string,
  options: {
    search?: string;
    includeArchived?: boolean;
    outletId?: string;
    categoryId?: string;
  } = {},
) {
  const params = buildExportParams(options);
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/items/export-csv?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    handleUnauthorizedResponse(response.status, err?.error?.message);
    throw new Error(
      err?.error?.message || `Export CSV failed (${response.status})`,
    );
  }
  const blob = await response.blob();
  const filename = extractFilename(response, "export.csv");
  return { blob, filename };
}

export async function exportItemsPdf(
  companyId: string,
  options: {
    search?: string;
    includeArchived?: boolean;
    outletId?: string;
    categoryId?: string;
  } = {},
) {
  const params = buildExportParams(options);
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/items/export-pdf?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    handleUnauthorizedResponse(response.status, err?.error?.message);
    throw new Error(
      err?.error?.message || `Export PDF failed (${response.status})`,
    );
  }
  const blob = await response.blob();
  const filename = extractFilename(response, "export.pdf");
  return { blob, filename };
}

export async function importItemsCsv(companyId: string, csvData: string) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/items/import-csv`,
    { csvData },
  );
  return response;
}

// Customers

export async function getCustomers(
  companyId: string,
  options: {
    type?: string;
    search?: string;
    customer_type_id?: string;
    pricing_tier_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  } = {},
) {
  const params = new URLSearchParams();
  if (options.type) params.append("type", options.type);
  if (options.search) params.append("search", options.search);
  if (options.customer_type_id)
    params.append("customer_type_id", options.customer_type_id);
  if (options.pricing_tier_id)
    params.append("pricing_tier_id", options.pricing_tier_id);
  if (options.status) params.append("status", options.status);
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.offset) params.append("offset", options.offset.toString());

  const response = await apiClient.get(
    `/api/companies/${companyId}/customers?${params.toString()}`,
  );
  return response as { customers: any[]; total: number };
}

export async function getCustomer(companyId: string, customerId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/customers/${customerId}`,
  );
  return response.customer;
}

export async function createCustomer(companyId: string, customerData: any) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/customers`,
    customerData,
  );
  return response as { customer: any; warning?: string };
}

export async function updateCustomer(
  companyId: string,
  customerId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/customers/${customerId}`,
    updates,
  );
  return response.customer;
}

export async function deleteCustomer(companyId: string, customerId: string) {
  const response = await apiClient.delete(
    `/api/companies/${companyId}/customers/${customerId}`,
  );
  return response as { customer: any; action: string };
}

export async function getCustomerSales(
  companyId: string,
  customerId: string,
  options: { limit?: number; offset?: number } = {},
) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.offset) params.append("offset", options.offset.toString());
  const response = await apiClient.get(
    `/api/companies/${companyId}/customers/${customerId}/sales?${params.toString()}`,
  );
  return response as { sales: any[]; total: number };
}

export async function getCustomerDocuments(
  companyId: string,
  customerId: string,
  options: { limit?: number; offset?: number } = {},
) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.offset) params.append("offset", options.offset.toString());
  const response = await apiClient.get(
    `/api/companies/${companyId}/customers/${customerId}/documents?${params.toString()}`,
  );
  return response as { documents: any[]; total: number };
}

export async function getCustomerRecentActivity(
  companyId: string,
  customerId: string,
) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/customers/${customerId}/recent-activity`,
  );
  return response as { items: any[] };
}

export async function checkDuplicateEmail(
  companyId: string,
  email: string,
  excludeId?: string,
) {
  const params = new URLSearchParams();
  params.append("email", email);
  if (excludeId) params.append("excludeId", excludeId);
  const response = await apiClient.get(
    `/api/companies/${companyId}/customers/check-duplicate?${params.toString()}`,
  );
  return response as { hasDuplicate: boolean; matchingCustomers: any[] };
}

// Users

export async function getUsers(
  companyId: string,
  options: {
    is_active?: boolean;
    role?: string;
    outletId?: string;
    search?: string;
  } = {},
) {
  const params = new URLSearchParams();
  if (options.is_active !== undefined)
    params.append("is_active", options.is_active.toString());
  if (options.role) params.append("role", options.role);
  if (options.outletId) params.append("outletId", options.outletId);
  if (options.search) params.append("search", options.search);

  const response = await apiClient.get(
    `/api/companies/${companyId}/users?${params.toString()}`,
  );
  return response.users;
}

export async function getUser(companyId: string, userId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/users/${userId}`,
  );
  return response.user;
}

function toUserPolicyError(error: any) {
  const code = error?.code;
  const message = error?.message || "";

  if (code === "VALIDATION_ERROR") {
    if (message.includes("already has an active admin")) {
      return new Error("Cette société a déjà un administrateur actif.");
    }
    if (message.includes("cannot create or assign role")) {
      return new Error(i18n.t("usersAdmin.errors.cannotAssignRole") as string);
    }
    if (message.includes("registers do not belong to this company")) {
      return new Error(
        "Un ou plusieurs caisses sélectionnées n'appartiennent pas à cette société.",
      );
    }
    if (message.includes("outside user's outlet assignments")) {
      return new Error(
        "Un utilisateur ne peut être affecté qu'aux caisses de ses points de vente assignés.",
      );
    }
  }

  return error;
}

export async function getCompanyOutlets(companyId: string) {
  const response = await apiClient.get(`/api/outlets?company_id=${companyId}`);
  return response.outlets || [];
}

export async function getCompanyRegisters(
  companyId: string,
  outletId?: string,
) {
  const params = new URLSearchParams();
  params.append("company_id", companyId);
  if (outletId) {
    params.append("outlet_id", outletId);
  }
  const response = await apiClient.get(`/api/registers?${params.toString()}`);
  return response.registers || [];
}

export async function getUserCashRegisters(companyId: string, userId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/users/${userId}/cash-registers`,
  );
  return response.assignments || [];
}

export async function setUserCashRegisters(
  companyId: string,
  userId: string,
  cashRegisterIds: string[],
  outletIds?: string[],
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/users/${userId}/cash-registers`,
    { cashRegisterIds, outletIds },
  );
  return response.assignments || [];
}

export async function createUser(companyId: string, userData: any) {
  try {
    const response = await apiClient.post(
      `/api/companies/${companyId}/users`,
      userData,
    );
    return response.user;
  } catch (error: any) {
    throw toUserPolicyError(error);
  }
}

export async function deleteUser(companyId: string, userId: string) {
  try {
    const response = await apiClient.delete(
      `/api/companies/${companyId}/users/${userId}`,
    );
    return response.user;
  } catch (error: any) {
    throw toUserPolicyError(error);
  }
}

export async function updateUser(
  companyId: string,
  userId: string,
  updates: any,
) {
  try {
    const response = await apiClient.put(
      `/api/companies/${companyId}/users/${userId}`,
      updates,
    );
    return response.user;
  } catch (error: any) {
    throw toUserPolicyError(error);
  }
}

// Payment Methods

export async function getPaymentMethods(
  companyId: string,
  options: { activeOnly?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (options.activeOnly !== undefined)
    params.append("activeOnly", options.activeOnly.toString());

  const response = await apiClient.get(
    `/api/companies/${companyId}/payment-methods?${params.toString()}`,
  );
  return response.paymentMethods;
}

export async function getPaymentMethod(
  companyId: string,
  paymentMethodId: string,
) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/payment-methods/${paymentMethodId}`,
  );
  return response.paymentMethod;
}

export async function createPaymentMethod(
  companyId: string,
  paymentMethodData: any,
) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/payment-methods`,
    paymentMethodData,
  );
  return response.paymentMethod;
}

export async function updatePaymentMethod(
  companyId: string,
  paymentMethodId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/payment-methods/${paymentMethodId}`,
    updates,
  );
  return response.paymentMethod;
}

// Price Grids

export async function getPriceGrids(
  companyId: string,
  options: { scope?: string; outletId?: string; activeOnly?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (options.scope) params.append("scope", options.scope);
  if (options.outletId) params.append("outletId", options.outletId);
  if (options.activeOnly !== undefined)
    params.append("activeOnly", options.activeOnly.toString());

  const response = await apiClient.get(
    `/api/companies/${companyId}/price-grids?${params.toString()}`,
  );
  return response.priceGrids;
}

export async function getPriceGrid(companyId: string, priceGridId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/price-grids/${priceGridId}`,
  );
  return response.priceGrid;
}

export async function createPriceGrid(companyId: string, priceGridData: any) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/price-grids`,
    priceGridData,
  );
  return response.priceGrid;
}

export async function updatePriceGrid(
  companyId: string,
  priceGridId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/price-grids/${priceGridId}`,
    updates,
  );
  return response.priceGrid;
}

export async function deletePriceGrid(companyId: string, priceGridId: string) {
  const response = await apiClient.delete(
    `/api/companies/${companyId}/price-grids/${priceGridId}`,
  );
  return response.priceGrid;
}

// Pricing Rules

export async function getPricingRules(companyId: string, priceGridId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/price-grids/${priceGridId}/rules`,
  );
  return response.pricingRules;
}

export async function getPricingRule(companyId: string, pricingRuleId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/pricing-rules/${pricingRuleId}`,
  );
  return response.pricingRule;
}

export async function createPricingRule(
  companyId: string,
  pricingRuleData: any,
) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/pricing-rules`,
    pricingRuleData,
  );
  return response.pricingRule;
}

export async function updatePricingRule(
  companyId: string,
  pricingRuleId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/pricing-rules/${pricingRuleId}`,
    updates,
  );
  return response.pricingRule;
}

export async function deletePricingRule(
  companyId: string,
  pricingRuleId: string,
) {
  const response = await apiClient.delete(
    `/api/companies/${companyId}/pricing-rules/${pricingRuleId}`,
  );
  return response.pricingRule;
}

// Price Resolution

export async function resolvePrice(
  companyId: string,
  itemId: string,
  options: { priceGridId: string; customerId?: string; effectiveDate?: string },
) {
  const params = new URLSearchParams();
  params.append("itemId", itemId);
  params.append("priceGridId", options.priceGridId);
  if (options.customerId) params.append("customerId", options.customerId);
  if (options.effectiveDate)
    params.append("effectiveDate", options.effectiveDate);

  const response = await apiClient.get(
    `/api/companies/${companyId}/prices/resolve?${params.toString()}`,
  );
  return response.price;
}

const adminService = {
  getItems,
  getItem,
  createItem,
  updateItem,
  archiveItem,
  unarchiveItem,
  exportItemsCsv,
  importItemsCsv,
  getCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  getCompanyOutlets,
  getCompanyRegisters,
  getUserCashRegisters,
  setUserCashRegisters,
  getPaymentMethods,
  getPaymentMethod,
  createPaymentMethod,
  updatePaymentMethod,
  getPriceGrids,
  getPriceGrid,
  createPriceGrid,
  updatePriceGrid,
  deletePriceGrid,
  getPricingRules,
  getPricingRule,
  createPricingRule,
  updatePricingRule,
  deletePricingRule,
  resolvePrice,
};

export default adminService;
